import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormAutocomplete from 'components/KNForm/KNFormAutocomplete'
import KNFormRadioGroup from 'components/KNForm/KNFormRadioGroup'
import type { KNOptionProps } from 'components/KNForm/types'
import { Role } from 'context/authentication/Role.types'
import type { CompaniesFilterValues } from 'context/CompaniesViewContext'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import type { WithId } from 'global/helpers/requests'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getFilteredCompanies } from 'screens/CompanyManager/CompanyManager.service'
import CompanyTypeLabel from 'screens/CompanyManager/CompanyTypeLabel'
import { useDebounce } from 'use-debounce'
import { grantRole, USERS_QUERY_KEY } from './UserManager.service'
import type { GrantRoleFormValues, UserData } from './UserManager.types'

interface GrantRoleDialogPayload {
  readonly user: UserData
}

interface GrantRoleDialogProps {
  readonly payload: GrantRoleDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const GrantRoleDialog = ({ payload, open, onAction, onClose }: GrantRoleDialogProps): ReactElement => {
  const { t } = useTranslation()
  const [companiesKeyword, setCompaniesKeyword] = useState<string>('')
  const [debouncedCompaniesKeyword] = useDebounce(companiesKeyword, 300)
  const [options, setOptions] = useState<KNOptionProps[]>([])
  const { handleSubmit, reset, control, formState, setError } = useForm<GrantRoleFormValues>()
  const queryClient = useQueryClient()
  const companiesQuery = useQuery({
    queryKey: ['companies', { debouncedCompaniesKeyword }],
    queryFn: () => getFilteredCompanies(0, { keywords: [debouncedCompaniesKeyword] } as CompaniesFilterValues),
    placeholderData: keepPreviousData,
  })
  const grantRoleMutation = useMutation({
    mutationFn: (data: WithId<GrantRoleFormValues>) => grantRole(data.id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      setCompaniesKeyword('')
      reset(
        processDefaultValues(
          {
            companyCid: null,
            role: null,
          },
          {
            companyCid: null,
          },
        ),
      )
    }
  }, [open])

  useEffect(() => {
    setOptions(
      companiesQuery.data?.content
        ?.filter((company) => !payload.user.companies?.find((userCompany) => company.cid === userCompany.cid))
        ?.map((company) => ({
          value: company.cid,
          label: company.displayName,
          payload: {
            type: company.type,
          },
        } as KNOptionProps)) ?? [],
    )
  }, [companiesQuery.data])

  const onSubmit: SubmitHandler<GrantRoleFormValues> = async (data: GrantRoleFormValues) => {
    await handleFormSubmission(() => grantRoleMutation.mutateAsync({ id: payload.user.cid, ...data }), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: onAction,
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={`${payload.user.displayName} — ${t('screens.shared.user_manager.grant_role.grant_role')}`}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
        >
          {t('screens.shared.user_manager.grant_role.grant')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={2}>
        <KNFormAutocomplete
          name="companyCid"
          label={t('screens.shared.user_manager.grant_role.company')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
          renderOption={(props, option): ReactElement => (
            <li {...props} key={option.value}>
              <Stack spacing={1} direction="row" alignItems="center">
                <CompanyTypeLabel type={option.payload.type} compact />
                <span>{option.label}</span>
              </Stack>
            </li>
          )}
          options={options}
          loading={companiesQuery.isPending}
          onInputChange={setCompaniesKeyword}
        />
        <KNFormRadioGroup
          name="role"
          label={t('screens.shared.user_manager.grant_role.role')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
          options={Object.keys(Role).map((role) => ({
            value: role,
            label: t(`screens.shared.user_manager.roles.${role}`),
          }))}
        />
      </Stack>
    </KNDialog>
  )
}

export default GrantRoleDialog
